import { CURRENCY_SYMBOLS } from "@/global/currencies"
import { ISO_LANGUAGE_CODE } from "@/i18n/constants"

const alphabethPattern = /[a-zA-Z]/g

export const INVALID_NUMERIC_INPUTS = [
  ``, // empty string
  `.`, // single dot
  `,`, // single comma
  `..`, // double dots
  `,,`, // double commas
  `...`, // triple dots
  `,,,`, // triple commas
  `.,.`, // mixed separators
  `.,`, // mixed separators
  `,.`, // mixed separators
  `,.,`, // mixed separators
] as const

const cleanInput = (value: string) => {
  const currencySymbolsPattern = new RegExp(
    `[${CURRENCY_SYMBOLS.join(``)}]`,
    `g`
  )
  const parsed = value
    .replace(currencySymbolsPattern, ``)
    .replace(alphabethPattern, ``)
    .replace(/[%]/, ``)
    .replace(/[" "]/g, ``)
    .trim()

  const hasParentheses = parsed.startsWith(`(`) && parsed.endsWith(`)`)

  if (hasParentheses) {
    return `-${parsed.slice(1, -1)}`
  }

  return parsed
}

export const handleMixedSeparators = (value: string) => {
  const parts = value.split(/[,.]/)

  // If we have exactly 3 parts, assume the last one is decimal
  if (parts.length === 3) {
    // Join all parts except last, then add decimal point and last part
    return `${parts.slice(0, -1).join(``)}.${parts[parts.length - 1]}`
  }

  // If we have more than 3 parts, handle as a special case
  if (parts.length > 3) {
    // Get the last part as decimal and join the rest
    const decimalPart = parts.pop()
    return `${parts.join(``)}.${decimalPart}`
  }
  if (parts.length === 2) {
    // For two parts, assume the last one is decimal
    return `${parts[0]}.${parts[1]}`
  }

  // For single part or empty string, return the value as is
  return parts.join(``)
}

const processDecimalPart = (value: string) => {
  // Handle regular thousand separators vs decimal separators
  let processedValue = value
  if (value.includes(`,`)) {
    const parts = value.split(`,`)
    // If the part after comma has exactly 2 digits, treat comma as decimal separator
    if (parts[1]?.length <= 2) {
      processedValue = value.replace(`,`, `.`)
    } else {
      // Otherwise, remove the comma (thousand separator)
      processedValue = value.replace(/,/g, ``)
    }
  }

  // Handle multiple decimal points
  const split = processedValue.split(`.`)
  const parsedHas2Commas = split.length > 1
  if (parsedHas2Commas) {
    const decimalPart = split.pop()
    return `${split.join(``)}.${decimalPart}`
  }
  if (split.length === 2) {
    if (split[1].length <= 3) {
      return `${split[0]}.${split[1]}`
    }
  }

  return processedValue
}

export const parseInputValue = (value: string) => {
  const parsed = cleanInput(value)

  // Early return for invalid inputs
  if (
    INVALID_NUMERIC_INPUTS.includes(
      parsed as (typeof INVALID_NUMERIC_INPUTS)[number]
    )
  ) {
    return ``
  }

  // First, handle the case where we have mixed separators
  if (parsed.includes(`,`) && parsed.includes(`.`)) {
    return handleMixedSeparators(parsed)
  }

  // Handle regular thousand separators vs decimal separators
  const processedValue = processDecimalPart(parsed)

  return processedValue
}

export const formatNumber = (
  value: number | string,
  options: Intl.NumberFormatOptions & {
    language?: ISO_LANGUAGE_CODE
  }
) => {
  const {
    currency,
    style,
    language,
    maximumFractionDigits,
    minimumFractionDigits,
    ...rest
  } = options
  const parsedValue = typeof value === `string` ? parseFloat(value) : value
  let valueNumber = Number.isNaN(parsedValue) ? 0 : parsedValue
  let minFractionDigits = minimumFractionDigits ?? 1
  if (style === `currency`) {
    minFractionDigits = minimumFractionDigits ?? 2
  }
  if (style === `percent`) {
    valueNumber /= 100
    minFractionDigits = minimumFractionDigits ?? 1
  }
  const formatter = new Intl.NumberFormat(language, {
    style: style ?? `decimal`,
    currency,
    compactDisplay: `short`,
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maximumFractionDigits ?? 2,
    ...rest,
  })
  return formatter.format(valueNumber)
}
